import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {symbol | null} */
  #mainDrawerPendingCloseId = null;

  /** @type {string | null} */
  #originalHeaderBackground = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    queueMicrotask(() => this.#assignMenuDrawerAnimationIndices());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#assignMenuDrawerAnimationIndices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#mainDrawerPendingCloseId = null;
      this.#assignMenuDrawerAnimationIndices();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    // Apply header drawer background color to header synchronously
    if (details === this.refs.details) {
      this.#applyHeaderColor();
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  openSearchModalFromDrawer() {
    document.documentElement.setAttribute('data-search-modal-open-from-drawer', '');
    const modal = document.querySelector('#search-modal');
    if (modal instanceof HTMLElement && 'showDialog' in modal && typeof modal.showDialog === 'function') {
      modal.dataset.dialogOpenedWithPointer = 'true';
      modal.showDialog();
    }
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Restore original header background color when main drawer closes
    if (details === this.refs.details) {
      this.#restoreHeaderColor();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const isMainDrawer = details === this.refs.details;
    const closeId = isMainDrawer ? Symbol('main-drawer-close') : null;
    if (isMainDrawer) {
      this.#mainDrawerPendingCloseId = closeId;
    }

    onAnimationEnd(
      drawer || details,
      () => {
        if (isMainDrawer && this.#mainDrawerPendingCloseId !== closeId) return;

        reset(details);
        if (isMainDrawer) {
          this.#mainDrawerPendingCloseId = null;
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Orders `--menu-drawer-animation-index` from DOM: optional `[data-drawer-item-animation]` before the list,
   * then each direct child of `ul.menu-drawer__menu` — either `[data-drawer-item-animation]` or
   * `[data-drawer-item-animation-container]` (then marked inner nodes), then consecutive
   * `[data-drawer-item-animation]` siblings after the list (featured products, custom append, etc.).
   *
   * @param {Element} menu
   */
  #assignIndicesForMenu(menu) {
    if (!(menu instanceof HTMLUListElement)) return 0;
    let index = 0;

    const prev = menu.previousElementSibling;
    if (prev instanceof HTMLElement && prev.hasAttribute('data-drawer-item-animation')) {
      index += 1;
      prev.style.setProperty('--menu-drawer-animation-index', String(index));
    }

    for (const child of menu.children) {
      if (!(child instanceof HTMLElement)) continue;

      if (child.hasAttribute('data-drawer-item-animation')) {
        index += 1;
        child.style.setProperty('--menu-drawer-animation-index', String(index));
        continue;
      }

      if (child.hasAttribute('data-drawer-item-animation-container')) {
        for (const inner of child.children) {
          if (!(inner instanceof HTMLElement)) continue;
          const target = inner.hasAttribute('data-drawer-item-animation')
            ? inner
            : inner.querySelector('[data-drawer-item-animation]');
          if (target instanceof HTMLElement) {
            index += 1;
            target.style.setProperty('--menu-drawer-animation-index', String(index));
          }
        }
      }
    }

    let next = menu.nextElementSibling;
    while (next instanceof HTMLElement && next.hasAttribute('data-drawer-item-animation')) {
      index += 1;
      next.style.setProperty('--menu-drawer-animation-index', String(index));
      next = next.nextElementSibling;
    }

    return index;
  }

  #assignMenuDrawerAnimationIndices() {
    const search = this.querySelector('.menu-drawer__search.menu-drawer__animated-element');
    if (search instanceof HTMLElement) {
      search.style.setProperty('--menu-drawer-animation-index', '0');
    }

    const rootMenu = this.querySelector('ul.menu-drawer__menu.has-submenu');
    let rootMax = 0;

    for (const menu of this.querySelectorAll('ul.menu-drawer__menu')) {
      const last = this.#assignIndicesForMenu(menu);
      if (menu === rootMenu) rootMax = last;
    }

    let nextIndex = rootMax + 1;
    const utility = this.querySelector('.menu-drawer__utility-links.menu-drawer__animated-element');
    if (utility instanceof HTMLElement) {
      utility.style.setProperty('--menu-drawer-animation-index', String(nextIndex));
      nextIndex += 1;
    }

    const menuDrawer = this.querySelector('.menu-drawer');
    const topFeatured = menuDrawer?.querySelector(':scope > .menu-drawer__featured-content[data-drawer-item-animation]');
    if (topFeatured instanceof HTMLElement) {
      topFeatured.style.setProperty('--menu-drawer-animation-index', String(nextIndex));
    }
  }

  /**
   * Apply header drawer background color to header component
   */
  #applyHeaderColor() {
    const headerComponent = document.querySelector('header-component');
    const headerDrawer = this.querySelector('[data-header-drawer]');
    
    if (!headerComponent || !headerDrawer) return;

    // Store original background if not already stored
    if (!this.#originalHeaderBackground) {
      const computedStyle = getComputedStyle(headerComponent);
      this.#originalHeaderBackground = computedStyle.backgroundColor;
    }

    // Get the drawer's background color
    const drawerStyle = getComputedStyle(headerDrawer);
    const drawerBg = drawerStyle.backgroundColor;

    // Apply to header component
    if (headerComponent instanceof HTMLElement) {
      headerComponent.style.backgroundColor = drawerBg;
    }
  }

  /**
   * Restore original header background color
   */
  #restoreHeaderColor() {
    const headerComponent = document.querySelector('header-component');
    
    if (!headerComponent || !this.#originalHeaderBackground) return;

    // Clear inline background to restore original styling
    if (headerComponent instanceof HTMLElement) {
      headerComponent.style.removeProperty('background-color');
    }
    
    // Clear stored background so it gets re-captured on next open
    this.#originalHeaderBackground = null;
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
